<template>
    <div id="login">
        <div
          v-if="banner.visible && banner.message"
          class="top-banner"
          :class="'top-banner--' + banner.type"
          role="alert"
        >
            <v-icon small color="white" class="mr-2">warning</v-icon>
            <span class="top-banner-text">{{ banner.message }}</span>
            <button
              type="button"
              class="top-banner-close"
              title="Fermer"
              @click="banner.visible = false"
            >
                <v-icon small color="white">close</v-icon>
            </button>
        </div>

        <div class="auth-page-root">
            <!-- Fond d'écran Blur inspiré de app.uska.fr -->
            <div class="background-blur">
                <div class="shape1"></div>
                <div class="shape2"></div>
                <div class="shape3"></div>
            </div>

            <div class="auth-container">
                <div class="row g-4 align-items-center justify-content-center w-100 m-0">
                    <!-- Colonne Gauche : Présentation Uska Rebranding -->
                    <div class="col-12 col-lg-6 col-xl-6 p-2 p-md-3">
                        <auth-presentation></auth-presentation>
                    </div>

                    <!-- Colonne Droite : Formulaire d'authentification -->
                    <div class="col-12 col-lg-5 col-xl-5 p-2 p-md-3">
                        <div class="auth-card">
                            <!-- En-tête de la carte -->
                            <div class="auth-card-header text-center mb-4">
                                <img src="/images/logo_uska_horizontal.svg" alt="Logo Uska" class="auth-form-logo mb-2" />
                                <h2 class="auth-title">Bienvenue !</h2>
                                <p class="auth-subtitle">Accédez à votre espace Office de Tourisme</p>
                            </div>

                            <!-- Erreurs SSO / Compte -->
                            <div v-if="noUser || noContract" class="mb-4">
                                <v-alert type="error" text class="rounded-lg mb-2" border="left" colored-border v-if="noUser">
                                    <strong>Compte introuvable :</strong> Vous ne possédez pas encore de compte actif. Merci de contacter votre administration ou notre support.
                                </v-alert>
                                <v-alert type="warning" text class="rounded-lg mb-2" border="left" colored-border v-if="noContract">
                                    <strong>Contrat expiré :</strong> Votre contrat est arrivé à son terme.
                                </v-alert>
                            </div>

                            <!-- Spinner SSO -->
                            <div v-if="loadingSso" class="text-center py-5">
                                <v-progress-circular indeterminate color="primary" size="48" width="4" class="mb-3"></v-progress-circular>
                                <p class="font-weight-medium text-dark mb-0">Connexion en cours...</p>
                            </div>

                            <!-- VUE 1 : Choix du mode de connexion (Dominante Apidae) -->
                            <div v-else-if="!showEmailForm" class="auth-mode-choice">
                                <!-- 1. ACTION DOMINANTE : Connexion Apidae -->
                                <div class="mb-3">
                                    <a :href="apiUrl + '/sso/attempt'" class="apidae-primary-btn">
                                        <div class="apidae-logo-box">
                                            <img src="/images/logo_apidae_carre.svg" alt="Apidae" class="apidae-btn-logo" />
                                        </div>
                                        <div class="apidae-btn-content">
                                            <span class="apidae-btn-title">Connexion Apidae</span>
                                            <span class="apidae-btn-sub">Authentification rapide pour Offices de Tourisme</span>
                                        </div>
                                        <v-icon color="#0284c7" class="apidae-arrow-icon">arrow_forward</v-icon>
                                    </a>
                                </div>

                                <!-- Séparateur doux -->
                                <div class="auth-divider my-4">
                                    <span>ou</span>
                                </div>

                                <!-- 2. ACTION SECONDAIRE : Connexion par e-mail -->
                                <div>
                                    <v-btn
                                      outlined
                                      block
                                      large
                                      color="#475569"
                                      class="email-secondary-btn text-none"
                                      @click="showEmailForm = true"
                                    >
                                        <v-icon left color="#64748b">email</v-icon>
                                        <span>Connexion par e-mail</span>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- VUE 2 : Formulaire de saisie Email & Mot de passe -->
                            <div v-else class="auth-email-form-view">
                                <div class="d-flex align-items-center mb-3">
                                    <v-btn icon small @click="showEmailForm = false" class="mr-2" title="Retour aux options">
                                        <v-icon small color="#64748b">arrow_back</v-icon>
                                    </v-btn>
                                    <span class="font-weight-bold text-dark" style="font-size: 0.95rem;">Connexion avec vos identifiants</span>
                                </div>

                                <form @submit.prevent="login">
                                    <div class="mb-3">
                                        <label class="input-label" for="email">Adresse email</label>
                                        <div class="modern-input-group">
                                            <v-icon class="input-icon-left">email</v-icon>
                                            <input
                                              v-model="email"
                                              type="email"
                                              name="email"
                                              placeholder="nom@destination.fr"
                                              class="custom-text-input email"
                                            />
                                        </div>
                                    </div>

                                    <div class="mb-2">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="input-label mb-0" for="password">Mot de passe</label>
                                            <router-link to="/forget-password" class="forgot-password-link">
                                                Mot de passe oublié ?
                                            </router-link>
                                        </div>
                                        <div class="modern-input-group">
                                            <v-icon class="input-icon-left">lock</v-icon>
                                            <input
                                              v-model="password"
                                              :type="displayPassword ? 'text' : 'password'"
                                              placeholder="••••••••"
                                              class="custom-text-input password"
                                              @keyup.enter="login()"
                                            />
                                            <button
                                              type="button"
                                              class="password-toggle-btn"
                                              @click="displayPassword = !displayPassword"
                                              title="Afficher/Masquer le mot de passe"
                                            >
                                                <v-icon small color="#64748b">{{ displayPassword ? 'visibility_off' : 'visibility' }}</v-icon>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Alertes & Erreurs -->
                                    <v-alert
                                      dense
                                      text
                                      type="error"
                                      class="mt-3 mb-3 rounded-lg"
                                      v-if="error"
                                    >
                                        {{ error }}
                                    </v-alert>

                                    <v-alert
                                      dense
                                      text
                                      type="success"
                                      class="mt-3 mb-3 rounded-lg"
                                      v-if="resetPassword"
                                    >
                                        Votre mot de passe a bien été réinitialisé.
                                    </v-alert>

                                    <!-- Bouton de validation -->
                                    <div class="mt-4">
                                        <v-btn
                                          depressed
                                          block
                                          large
                                          color="primary"
                                          class="auth-submit-btn text-none"
                                          :loading="startSpinner"
                                          :disabled="!email || !password || startSpinner"
                                          @click="login()"
                                        >
                                            <span>Se connecter</span>
                                            <v-icon right small>arrow_forward</v-icon>
                                        </v-btn>
                                    </div>

                                    <div class="mt-3 text-center">
                                        <button type="button" @click="showEmailForm = false" class="back-to-choice-btn">
                                            ← Revenir au choix de connexion
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <!-- Footer assistance -->
                            <div class="auth-card-footer mt-4 text-center">
                                <p class="mb-1 small text-muted">
                                    Vous n'avez pas encore de compte ?
                                    <a href="https://intercom.help/dahub/fr/articles/8499389-foire-aux-questions-connexion" target="_blank" class="font-weight-semibold">
                                        Aide à la création
                                    </a>
                                </p>
                                <p class="mb-0 small text-muted">
                                    Vous n'êtes pas un Office de Tourisme ?
                                    <a href="https://www.uska.fr/contact" target="_blank" class="font-weight-semibold">
                                        Contactez notre équipe
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapState } from "vuex"
import AuthPresentation from "./auth/AuthPresentation.vue"

export default {
    name: "Login",
    components: {
        AuthPresentation
    },
    data() {
        return {
            apiUrl: process.env.VUE_APP_API_URL,
            email: null,
            password: null,
            submitted: false,
            error: null,
            startSpinner: false,
            displayPassword: false,
            resetPassword: false,
            showEmailForm: false,
            noUser: false,
            noContract: false,
            loadingSso: false,
            banner: {
                visible: false,
                type: 'info',          // 'info' | 'warning' | 'error'
                message: "La connexion via Apidae est rétablie."
            },
        }
    },
    methods: {
        ...mapMutations('account', ['setToken']),
        async login() {
            if (!this.email || !this.password) return
            this.submitted = true
            this.error = null
            this.startSpinner = true

            try {
                await this.$store.dispatch('account/login', [this.email, this.password])
                await this.$store.dispatch('account/getCredentials')

                if (this.user && this.user.cgu_validation) {
                    this.$router.push('/home')
                } else {
                    this.$router.push('/accept-cgu')
                }

                this.startSpinner = false
            } catch (e) {
                this.error = "Email ou mot de passe incorrect"
                this.startSpinner = false
            }
        }
    },
    computed: {
        ...mapState('account', ['user'])
    },
    async created() {
        let query = this.$route.query
        this.resetPassword = query.resetPassword !== undefined

        if (query.mode === 'email') {
            this.showEmailForm = true
        }

        if (query.error !== undefined && query.error === 'no_user') {
            this.noUser = true
        }

        if (query.error !== undefined && query.error === 'no_contract') {
            this.noContract = true
        }

        if (query.token !== undefined && !this.noContract) {
            this.loadingSso = true
            this.setToken(query.token)

            try {
                await this.$store.dispatch('account/getCredentials')

                if (!this.user.cgu_validation) {
                    this.$router.push('/accept-cgu')
                } else {
                    this.$router.push('/home')
                }
            } catch (e) {
                this.loadingSso = false
            }
        }
    }
}
</script>